import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Unlock, Zap, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import candidateUnlockService from '../../../services/candidateUnlockService';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../utils/cn';

const CandidateUnlockModal = ({ application, companyId, onClose, onUnlockSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockStatus, setUnlockStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUnlockStatus();
  }, [companyId]);

  const loadUnlockStatus = async () => {
    try {
      setLoading(true);
      const result = await candidateUnlockService?.canUnlockCandidate(companyId);
      setUnlockStatus(result);
    } catch (err) {
      setError('Failed to load unlock status');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    try {
      setUnlocking(true);
      setError(null);

      const result = await candidateUnlockService?.unlockCandidate(
        companyId,
        application?.id,
        user?.id
      );

      if (result?.success) {
        onUnlockSuccess?.(application?.id);
        onClose();
      } else {
        setError(result?.error || 'Failed to unlock candidate');
      }
    } catch (err) {
      setError('An error occurred while unlocking');
    } finally {
      setUnlocking(false);
    }
  };

  const handleUpgrade = () => {
    window.location.href = `/company-dashboard-hub/${companyId}/billing`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </Card>
      </div>
    );
  }

  const canUnlock = unlockStatus?.canUnlock;
  const remaining = unlockStatus?.remaining;
  const plan = unlockStatus?.plan;
  const isFreemium = plan === 'freemium';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md"
      >
        <Card className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                canUnlock ? "bg-blue-100" : "bg-yellow-100"
              )}>
                {canUnlock ? (
                  <Unlock className="w-6 h-6 text-blue-600" />
                ) : (
                  <Lock className="w-6 h-6 text-yellow-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {canUnlock ? 'Unlock Candidate' : 'No Unlocks Remaining'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isFreemium && `${remaining} unlock${remaining !== 1 ? 's' : ''} remaining this month`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              ×
            </button>
          </div>

          {/* Content */}
          {canUnlock ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  Unlock to view full candidate details including:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-blue-800">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Full name and contact information</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Resume and cover letter</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Work experience and skills</span>
                  </li>
                </ul>
              </div>

              {isFreemium && remaining === 1 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-900">
                        This is your last free unlock this month
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Resets on {new Date(unlockStatus?.resetAt)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-900">{error}</p>
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  onClick={handleUnlock}
                  disabled={unlocking}
                  className="flex-1"
                >
                  {unlocking ? 'Unlocking...' : 'Use Unlock'}
                </Button>
                <Button onClick={onClose} variant="outline">
                  Cancel
                </Button>
              </div>

              {isFreemium && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-3">
                    Want unlimited access to all candidates?
                  </p>
                  <Button
                    onClick={handleUpgrade}
                    variant="outline"
                    className="w-full"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade to Standard - $100/month
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">
                      You have used your {unlockStatus?.limit} free unlock this month
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Next unlock available: {new Date(unlockStatus?.resetAt)?.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  Upgrade to Standard for:
                </p>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Unlimited candidate access</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Full details in all emails</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Up to 3 team members</span>
                  </li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleUpgrade} className="flex-1">
                  <Zap className="w-4 h-4 mr-2" />
                  Upgrade Now - $100/month
                </Button>
                <Button onClick={onClose} variant="outline">
                  Close
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default CandidateUnlockModal;